import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Hint } from "./icon-button";
import { RowMenu, type MenuEntry } from "./row-menu";
import { useI18n } from "../../i18n";

/** Canon `.row` / `.maprow`: a list or rail line, px-1.5 py-[5px], 4px, hover panel-2. `current` = the accent-soft
 *  selected line (aria-current). Use `<Row as="button">` for clickable lines. */
export const ROW_CLASS =
  "flex w-full min-w-0 items-center gap-2 rounded px-1.5 py-[5px] text-left text-body-s transition-colors hover:bg-panel-2 aria-[current=true]:bg-hl/13 aria-[current=true]:ring-1 aria-[current=true]:ring-inset aria-[current=true]:ring-hl/45";

type RowProps<T extends React.ElementType> = { as?: T; current?: boolean } & Omit<React.ComponentPropsWithoutRef<T>, "as">;

export function Row<T extends React.ElementType = "div">({ as, current, className, ...props }: RowProps<T>) {
  const Comp: React.ElementType = as ?? "div";
  return <Comp aria-current={current ? "true" : undefined} className={cn(ROW_CLASS, className)} {...props} />;
}

/** A decoration of a row (П4): only the deviation from the norm — a lock «только мастер», an eye «видят игроки». */
export interface RowDeco {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly testid?: string;
}

export interface ListRowProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  readonly as?: "li" | "div";
  /** The name, one line with an ellipsis; the full name in the hint (`tip`, default = the name). */
  readonly label: React.ReactNode;
  readonly tip?: string;
  /** The main safe action of the row = a click on the name (open for me, open the record, open the sheet) — П3. */
  readonly onOpen?: () => void;
  readonly onOpenDouble?: () => void;
  readonly labelTestid?: string;
  readonly labelClassName?: string;
  /** Replaces the name button (an inline rename field). */
  readonly labelSlot?: React.ReactNode;
  /** Tree: a twisty on the left (`null` keeps its place empty — only when a sibling has one); depth indents by 16 px a
   *  level (П9; text lines 14 / 30 / 46). */
  readonly twisty?: { readonly open: boolean; readonly onToggle: () => void } | null;
  readonly depth?: number;
  readonly lead?: React.ReactNode;
  readonly dot?: string;
  readonly pills?: React.ReactNode;
  readonly deco?: readonly RowDeco[];
  /** `text-pill` dim on the right: the status, «CR 10», «1/3». */
  readonly meta?: React.ReactNode;
  /** A state the row is in AND its switch, always visible (the journal record's eye: VisibilityToggle). */
  readonly state?: React.ReactNode;
  /** One main action icon (IconButton size="sm") — shown on hover / focus, always on the current row. */
  readonly actions?: React.ReactNode;
  /** «⋯»: every action in words, the dangerous one red with a confirmation (П5). */
  readonly menu?: readonly MenuEntry[];
  readonly menuTestid?: string;
  readonly current?: boolean;
  /** The actions stay visible (an action that is the row's point — «Запустить у всех»). */
  readonly alwaysActions?: boolean;
  /** Under the row (a confirmation, an inline form). */
  readonly after?: React.ReactNode;
}

/**
 * ONE list line of the sidebar (brief П2 + П3): 28 px, 13 px, name with an ellipsis; on the right the meta, the deviation
 * marks, then one action icon and «⋯» that appear on hover and focus (always on the current row and on touch screens).
 */
export const ListRow = React.forwardRef<HTMLElement, ListRowProps>(function ListRow(
  {
    as = "li",
    label,
    tip,
    onOpen,
    onOpenDouble,
    labelTestid,
    labelClassName,
    labelSlot,
    twisty,
    depth = 0,
    lead,
    dot,
    pills,
    deco,
    meta,
    state,
    actions,
    menu,
    menuTestid,
    current,
    alwaysActions,
    after,
    className,
    style,
    ...rest
  },
  ref,
) {
  const { t } = useI18n();
  const Comp = as as React.ElementType;
  const title = tip ?? (typeof label === "string" ? label : undefined);
  const nameClass = cn("list-row__name", labelClassName);
  return (
    <>
      <Comp
        ref={ref}
        className={cn("list-row", className)}
        aria-current={current ? "true" : undefined}
        style={depth > 0 ? { paddingLeft: 6 + depth * 16, ...style } : style}
        {...rest}
      >
        {twisty === undefined ? null : twisty === null ? (
          <span className="list-row__twisty list-row__twisty--none" aria-hidden="true" />
        ) : (
          <button
            type="button"
            className="list-row__twisty"
            aria-expanded={twisty.open}
            aria-label={twisty.open ? t("sb.collapse") : t("sb.expand")}
            onClick={(e) => {
              e.stopPropagation();
              twisty.onToggle();
            }}
          >
            {twisty.open ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
          </button>
        )}
        {lead !== undefined && lead !== null ? <span className="list-row__lead">{lead}</span> : null}
        {dot ? <span className="list-row__dot" style={{ background: dot }} aria-hidden="true" /> : null}
        {labelSlot ??
          (onOpen ? (
            <button
              type="button"
              className={nameClass}
              title={title}
              data-testid={labelTestid}
              onClick={onOpen}
              onDoubleClick={onOpenDouble}
            >
              {label}
            </button>
          ) : (
            <span className={nameClass} title={title} data-testid={labelTestid}>
              {label}
            </span>
          ))}
        {pills}
        {deco?.map((d, i) => (
          <Hint key={i} text={d.label}>
            <span className="list-row__deco" tabIndex={0} role="img" aria-label={d.label} data-testid={d.testid}>
              {d.icon}
            </span>
          </Hint>
        ))}
        {/* A plain space keeps the name and the meta apart for a reader of the row's text (it is not drawn in a flex row). */}
        {meta !== undefined && meta !== null && meta !== "" ? <>{" "}<span className="list-row__meta">{meta}</span></> : null}
        {state}
        {actions || (menu && menu.length > 0) ? (
          <span className="list-row__acts" data-always={alwaysActions || undefined}>
            {actions}
            {menu && menu.length > 0 ? <RowMenu items={menu} testid={menuTestid} /> : null}
          </span>
        ) : null}
      </Comp>
      {after}
    </>
  );
});

/** The «Показать ещё …» line under a list (П8): the words are the caller's. */
export function ListMoreButton({ children, onClick, testid }: { readonly children: React.ReactNode; readonly onClick: () => void; readonly testid?: string }): React.JSX.Element {
  return (
    <button type="button" className="list-more" onClick={onClick} data-testid={testid}>
      {children}
    </button>
  );
}

/** «Показать ещё N» under a list (П8) and «Свернуть до 12» back. */
export function ShowMore({
  rest,
  expanded,
  limit,
  onMore,
  onLess,
  testid,
}: {
  readonly rest: number;
  readonly expanded: boolean;
  readonly limit: number;
  readonly onMore: () => void;
  readonly onLess: () => void;
  readonly testid?: string;
}): React.JSX.Element | null {
  const { t } = useI18n();
  if (rest > 0)
    return (
      <button type="button" className="list-more" onClick={onMore} data-testid={testid}>
        {t("sb.showMore").replace("{n}", String(rest))}
      </button>
    );
  if (expanded)
    return (
      <button type="button" className="list-more" onClick={onLess} data-testid={testid}>
        {t("sb.showLess").replace("{n}", String(limit))}
      </button>
    );
  return null;
}

/** П8: in a group the first 12 rows, then «Показать ещё N». 12 = half the visible panel at 1280 (386 px) in 28 px rows, minus the button. */
export const LIST_LIMIT = 12;

export function useShowMore<T>(items: readonly T[], limit: number = LIST_LIMIT, all = false): {
  readonly shown: readonly T[];
  readonly more: React.JSX.Element | null;
} {
  const [expanded, setExpanded] = React.useState(false);
  const open = expanded || all;
  const shown = open ? items : items.slice(0, limit);
  const more = all ? null : (
    <ShowMore rest={items.length - shown.length} expanded={expanded && items.length > limit} limit={limit} onMore={() => setExpanded(true)} onLess={() => setExpanded(false)} />
  );
  return { shown, more };
}

/** The same, for a render site that cannot call a hook per group: the expanded keys live in the caller. */
export function limitList<T>(
  key: string,
  items: readonly T[],
  expanded: ReadonlySet<string>,
  setExpanded: (key: string, open: boolean) => void,
  all = false,
  limit: number = LIST_LIMIT,
): { readonly shown: readonly T[]; readonly more: React.JSX.Element | null } {
  const open = all || expanded.has(key);
  const shown = open ? items : items.slice(0, limit);
  return {
    shown,
    more: all ? null : (
      <ShowMore
        rest={items.length - shown.length}
        expanded={expanded.has(key) && items.length > limit}
        limit={limit}
        onMore={() => setExpanded(key, true)}
        onLess={() => setExpanded(key, false)}
      />
    ),
  };
}

/** The search over a list (П8) and the type chips (Все / Квесты / NPC / Лор / Листы), like the letopis `.chip`. */
export function ListFinder({
  value,
  onChange,
  placeholder,
  chips,
  chip,
  onChip,
  testid,
  className,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly chips?: readonly { readonly id: string; readonly label: string; readonly count?: number }[];
  readonly chip?: string;
  readonly onChip?: (id: string) => void;
  readonly testid?: string;
  readonly className?: string;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className={cn("list-find", className)}>
      <input
        type="search"
        className="list-find__input"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
      />
      {chips && chips.length > 0 ? (
        <div className="list-find__chips" role="group" aria-label={t("sb.filter")}>
          {chips.map((c) => (
            <button key={c.id} type="button" className="list-chip" aria-pressed={(chip ?? "all") === c.id} onClick={() => onChip?.(c.id)}>
              {c.label}
              {c.count !== undefined ? <span className="list-chip__n">{c.count}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
